import { z } from 'zod';
import { MoneySchema } from '../../../models/Money/schemas';
import {
  AddressWithPhoneNumberSchema,
  BasicAddressSchema
} from '../../../models/Address/schemas';
import { PaymentSummarySchema } from '../../../models/Order/PaymentSummary/schemas';
import {
  BasicShipmentSchema,
  MaskedShipmentSchema,
  ShipmentSchema
} from '../../../models/Shipment/schemas';
import type { IHistoricOrder } from './IHistoricOrder';
import { OrderType, type IMaskedHistoricOrder } from './IMaskedHistoricOrder';
import OrderInfoLevel from './OrderInfoLevel';
import type { IMaskedBasicHistoricOrder } from './IMaskedBasicHistoricOrder';
import type { IBasicHistoricOrder } from './IBasicHistoricOrder';

/** Zod schema for {@link IMaskedBasicHistoricOrder}. */
export const MaskedBasicHistoricOrderSchema = z.object({
  orderType: z.nativeEnum(OrderType),
  orderID: z.string(),
  orderDate: z.string(),
  shipments: z.array(BasicShipmentSchema),
  total: MoneySchema.nullable(),
  totalItems: z.number(),
  // Avoid using email validation here, as emails in masked orders are not
  // considered valid by zod (e.g. 'ab***@***.com').
  email: z.string(),
  infoLevel: z.nativeEnum(OrderInfoLevel)
}) satisfies z.ZodType<IMaskedBasicHistoricOrder>;

/** Zod schema for {@link IBasicHistoricOrder}. */
export const BasicHistoricOrderSchema = MaskedBasicHistoricOrderSchema.extend({
  billingAddress: BasicAddressSchema
}) satisfies z.ZodType<IBasicHistoricOrder>;

/** Zod schema for {@link IMaskedHistoricOrder}. */
export const MaskedHistoricOrderSchema = MaskedBasicHistoricOrderSchema.extend({
  shipments: z.array(MaskedShipmentSchema),
  subtotal: MoneySchema.nullable(),
  tax: MoneySchema.nullable(),
  shippingCost: MoneySchema.nullable(),
  discount: MoneySchema.nullable(),
  giftCardTotal: MoneySchema.nullable()
}) satisfies z.ZodType<IMaskedHistoricOrder>;

/** Zod schema for {@link IHistoricOrder}. */
export const HistoricOrderSchema = MaskedHistoricOrderSchema.extend({
  orderToken: z.string(),
  billingAddress: AddressWithPhoneNumberSchema,
  shipments: z.array(ShipmentSchema),
  payments: z.array(PaymentSummarySchema)
}) satisfies z.ZodType<IHistoricOrder>;

/**
 * Union schema that matches any supported historic order shape (masked or
 * full). Extend this union whenever a new IMaskedHistoricOrder-compatible
 * variant is introduced.
 */
export const HistoricOrderUnionSchema = z.union([
  MaskedHistoricOrderSchema,
  HistoricOrderSchema
]);

export const BasicHistoricOrderUnionSchema = z.union([
  MaskedBasicHistoricOrderSchema,
  BasicHistoricOrderSchema
]);
